/**
 * api.ts — React-side API access (task A05).
 *
 * The client never duplicates DTO shapes: everything is imported from the
 * shared contracts (`@shared`). The CSRF token is kept in memory only —
 * never in localStorage/sessionStorage (TODO.md: do not persist session
 * material client-side). The session token itself never reaches this code
 * (HttpOnly cookie).
 */
import {
  ApiClient,
  ApiClientError,
  type AssignmentRecipient,
  type CreateGameRequest,
  type CreateGameResponseData,
  type CreateWishlistRequest,
  type GameDetail,
  type JoinGameRequest,
  type ListGamesResponseData,
  type LoginRequest,
  type LoginResponseData,
  type ReadyResponseData,
  type RegisterRequest,
  type RegisterResponseData,
  type SessionResponseData,
  type SnowPairsResponseData,
  type WishlistListResponseData,
} from '@shared';

/** In-memory CSRF token holder (cleared on logout/page reload). */
class CsrfTokenHolder {
  private token: string | null = null;

  set(token: string | null): void {
    this.token = token;
  }

  get(): string | null {
    return this.token;
  }
}

export const csrfToken = new CsrfTokenHolder();

// The fetch wrapper is resolved lazily (globalThis at call time) so test
// harnesses can stub the global fetch after module load.
export const api = new ApiClient(
  (url, init) => globalThis.fetch(url, init),
  () => csrfToken.get(),
);

/** Typed convenience wrappers used by the React screens (B10+). */
export const apiV1 = {
  register: (body: RegisterRequest) =>
    api.request<RegisterResponseData>('POST', '/auth/register', body),
  login: (body: LoginRequest) =>
    api.request<LoginResponseData>('POST', '/auth/login', body).then((data) => {
      csrfToken.set(data.csrfToken);
      return data;
    }),
  session: () =>
    api.request<SessionResponseData>('GET', '/auth/session').then((data) => {
      csrfToken.set(data.csrfToken);
      return data;
    }),
  logout: () => api.request<void>('POST', '/auth/logout'),

  listGames: () => api.request<ListGamesResponseData>('GET', '/games'),
  createGame: (body: CreateGameRequest) =>
    api.request<CreateGameResponseData>('POST', '/games', body),
  joinGame: (body: JoinGameRequest) => api.request<GameDetail>('POST', '/games/join', body),
  getGame: (gameId: number) => api.request<GameDetail>('GET', `/games/${gameId}`),
  markReady: (gameId: number) => api.request<ReadyResponseData>('POST', `/games/${gameId}/ready`),
  startGame: (gameId: number) => api.request<GameDetail>('POST', `/games/${gameId}/start`),
  myAssignments: (gameId: number) =>
    api.request<AssignmentRecipient[]>('GET', `/games/${gameId}/my-assignments`),

  /** Snow overlay: creator-only full pairing map (server-enforced). */
  snowPairs: (gameId: number) =>
    api.request<SnowPairsResponseData>('GET', `/games/${gameId}/snow`),

  getMyWishlist: (gameId: number) =>
    api.request<WishlistListResponseData>('GET', `/games/${gameId}/wishlist/me`),
  createWishlistItem: (gameId: number, body: CreateWishlistRequest) =>
    api.request<{ id: number }>('POST', `/games/${gameId}/wishlist`, body),
  deleteWishlistItem: (gameId: number, itemId: number) =>
    api.request<void>('DELETE', `/games/${gameId}/wishlist/${itemId}`),
  getPlayerWishlist: (gameId: number, userId: number) =>
    api.request<WishlistListResponseData>('GET', `/games/${gameId}/players/${userId}/wishlist`),
};

export { ApiClientError };
